import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";

// Time-window filter shared by the admin drill-in pages (created games, new
// accounts, active hosts). The selection lives in the URL — ?window=1w|1m|2m|3m
// or ?from=YYYY-MM-DD&to=YYYY-MM-DD for a custom range — so filtered views are
// linkable and survive reload. Pages read the effective range via
// useTimeWindow() and pass it straight to the API.

const PRESETS = [
    { key: "1w", label: "Last week", days: 7 },
    { key: "1m", label: "Last month", months: 1 },
    { key: "2m", label: "Last 2 months", months: 2 },
    { key: "3m", label: "Last 3 months", months: 3 },
];
const DEFAULT_KEY = "1m";

// Local-date YYYY-MM-DD (toISOString would shift the day near midnight).
function fmtDate(d) {
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${m}-${day}`;
}

function presetRange({ days = 0, months = 0 }) {
    const to = new Date();
    const from = new Date();
    from.setMonth(from.getMonth() - months);
    from.setDate(from.getDate() - days);
    return { from: fmtDate(from), to: fmtDate(to) };
}

export function useTimeWindow() {
    const [searchParams, setSearchParams] = useSearchParams();
    const fromParam = searchParams.get("from");
    const toParam = searchParams.get("to");

    let key, from, to;
    if (fromParam && toParam) {
        key = "custom";
        from = fromParam;
        to = toParam;
    } else {
        const requested = searchParams.get("window");
        const preset = PRESETS.find((p) => p.key === requested) || PRESETS.find((p) => p.key === DEFAULT_KEY);
        key = preset.key;
        ({ from, to } = presetRange(preset));
    }

    // Replace rather than push: filter tweaks shouldn't pile up history
    // entries the back button then has to unwind.
    const setPreset = (presetKey) => setSearchParams({ window: presetKey }, { replace: true });
    const setCustomRange = (f, t) => setSearchParams({ from: f, to: t }, { replace: true });

    return { key, from, to, setPreset, setCustomRange };
}

const TimeWindowFilter = () => {
    const { key, from, to, setPreset, setCustomRange } = useTimeWindow();
    const [showCustom, setShowCustom] = useState(key === "custom");
    const [draftFrom, setDraftFrom] = useState(from);
    const [draftTo, setDraftTo] = useState(to);

    const openCustom = () => {
        setDraftFrom(from);
        setDraftTo(to);
        setShowCustom(true);
    };

    const applyCustom = (e) => {
        e.preventDefault();
        if (draftFrom && draftTo && draftFrom <= draftTo) {
            setCustomRange(draftFrom, draftTo);
        }
    };

    return (
        <div className="d-flex flex-wrap align-items-center gap-3 justify-content-center mb-3">
            <ButtonGroup size="sm">
                {PRESETS.map((p) => (
                    <Button
                        key={p.key}
                        variant={key === p.key ? "primary" : "outline-primary"}
                        onClick={() => { setShowCustom(false); setPreset(p.key); }}
                    >
                        {p.label}
                    </Button>
                ))}
                <Button
                    variant={key === "custom" ? "primary" : "outline-primary"}
                    onClick={openCustom}
                >
                    Custom
                </Button>
            </ButtonGroup>
            {showCustom && (
                <Form onSubmit={applyCustom} className="d-flex align-items-center gap-2">
                    <Form.Control
                        type="date"
                        size="sm"
                        value={draftFrom}
                        max={draftTo}
                        onChange={(e) => setDraftFrom(e.target.value)}
                        aria-label="From date"
                    />
                    <span className="text-muted">to</span>
                    <Form.Control
                        type="date"
                        size="sm"
                        value={draftTo}
                        min={draftFrom}
                        onChange={(e) => setDraftTo(e.target.value)}
                        aria-label="To date"
                    />
                    <Button type="submit" size="sm" variant="primary">
                        Apply
                    </Button>
                </Form>
            )}
        </div>
    );
};

export default TimeWindowFilter;
